import React, { useEffect } from 'react';
import { List } from 'immutable';
import { handleCtiClick } from 'flow/actions/cti-actions';
import CtiActionButton from 'flow/components/integration/IntegrationActionButton';
import Icon from 'flow/components/ui-components/Icon';
import Loading from 'flow/components/ui-components/Loading';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    allowCtiCommunication,
    getIntegrationConfig,
    getIntegrationContactsFromStateByNumber,
    getSelectedCtiContactByNumber,
    wrapperIsRegistered,
} from 'flow/selectors/cti-integration-selectors';
import { LOOKUP_STATUS } from 'flow/types/lookup-status-types';
import { addCtiLogEntry, anyOptionalKeyDown } from 'flow/utils/browser-utils';
import { getCtiHostIcon } from 'flow/utils/cti-config-utils';
import { sortOtherRoutesByStatusAndTimestamp } from 'flow/utils/cti-sorting-utils.js';
import { getLookupStatusByContactsSet } from 'flow/utils/cti-status-lookup-utils';
import { getTextLabel } from 'flow/utils/string-utils';
import CtiLinkIcon from './CtiLinkIcon';
import IntegrationContactSelector from './IntegrationContactSelector';

interface Button {
    label: string;
    link: string;
    altLink?: string;
}

interface OtherRoute {
    routeName: string;
    routeNumber: number;
    routeUrl: string;
    routeLink: string;
    type: string;
}

interface Props {
    remoteNumber: string;
    localNumber: string;
    direction: string;
}

const limitOtherRoutes = (otherRoutes: OtherRoute[]) =>
    sortOtherRoutesByStatusAndTimestamp(otherRoutes).slice(0, 5);

const CtiIntegrationWidget = ({
    remoteNumber,
    localNumber,
    direction,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const integrationConfig = useAppSelector(getIntegrationConfig);
    const isLoaded = useAppSelector(wrapperIsRegistered);
    const isCtiAllowed = useAppSelector(allowCtiCommunication);
    const contactsMatchingNumber = useAppSelector((state) =>
        getIntegrationContactsFromStateByNumber(state, remoteNumber),
    );
    const selectedCtiContact = useAppSelector((state) =>
        getSelectedCtiContactByNumber(state, remoteNumber),
    );

    const contactRoute = selectedCtiContact?.get('contactRoute') ?? null;
    const defaultButtons = integrationConfig
        .get('defaultButtons', List())
        .toJS();
    const hostDescription = integrationConfig.get('hostDescription');
    const hostImgUrl = integrationConfig.get('hostImgUrl');
    const contactRouteButtons = selectedCtiContact
        ? selectedCtiContact.get('contactRouteButtons', List()).toJS()
        : [];
    const contactRouteUrl = selectedCtiContact
        ? selectedCtiContact.get('routeUrl', null)
        : null;
    const otherRoutes = selectedCtiContact
        ? selectedCtiContact.get('otherRoutes', List()).toJS()
        : [];
    const typeLabel = selectedCtiContact
        ? selectedCtiContact.get('typeLabel')
        : null;
    const lookupStatus = contactsMatchingNumber
        ? getLookupStatusByContactsSet(contactsMatchingNumber)
        : 'pending';

    useEffect(() => {
        addCtiLogEntry('Presenting CTI widget');
    }, []);

    useEffect(() => {
        if (contactRoute !== null) {
            addCtiLogEntry('CTI widget has contact decoration');
        }
    }, [contactRoute]);

    const getLinkOnClick = (
        link: string,
        routeUrl: string | null,
        altLink?: string,
    ) => {
        return link || altLink
            ? (e: React.MouseEvent) => {
                  const optionalOpenMethod = anyOptionalKeyDown(e);
                  const linkToUse =
                      optionalOpenMethod && altLink ? altLink : link;
                  if (optionalOpenMethod && routeUrl) {
                      // If the wrapper has supplied a route Url for the clicked link
                      // and the user is intending to use an optional opening method
                      // we should simply allow the browser to handle the event since
                      // we don't want to code around browser differences and user
                      // settings for anchor tags.
                  } else {
                      // Prevent the browser from redirecting and run the onLink function.
                      e.preventDefault();
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();

                      dispatch(
                          handleCtiClick(linkToUse, localNumber, {
                              direction,
                              number: remoteNumber,
                          }),
                      );
                  }
              }
            : null;
    };

    const getHostIcon = () => {
        const integrationIcon = getCtiHostIcon(hostDescription);
        const icon =
            integrationIcon ||
            // Decorative: hostDescription is rendered as text beside it.
            (hostImgUrl ? <img src={hostImgUrl} alt="" /> : null);
        return <div className="ms-2 h-5 rounded-xs">{icon}</div>;
    };

    const getStatusText = () => {
        if (!isCtiAllowed) {
            return i18next.t('cti.widget.status.not_allowed');
        }

        if (!isLoaded) {
            return i18next.t('cti.widget.status.connecting', {
                hostSystemDescription: hostDescription,
            });
        }

        switch (lookupStatus) {
            case LOOKUP_STATUS.SUCCESS: {
                if (typeLabel) {
                    const label = i18next.t(typeLabel);
                    return label
                        ? i18next.t('cti.widget.status.success.dynamic', {
                              label,
                          })
                        : i18next.t('cti.widget.status.success');
                }
                return i18next.t('cti.widget.status.success');
            }
            case LOOKUP_STATUS.FAILURE:
                return i18next.t('cti.widget.status.failure');
            case LOOKUP_STATUS.PENDING:
                return i18next.t('cti.widget.status.pending');
            default:
                return '';
        }
    };

    const getStatusIcon = () => {
        if (!isCtiAllowed) {
            return null;
        }

        if (!isLoaded) {
            return (
                <div>
                    <Loading size={18} disableTimeout={true} />
                </div>
            );
        }

        switch (lookupStatus) {
            // Status, not an action: these report how the contact lookup went
            // and have no onClick. As buttons they were keyboard stops that
            // did nothing.
            case LOOKUP_STATUS.SUCCESS:
                return (
                    <span className="flex size-4 items-center justify-center pe-1 text-green-500">
                        <Icon iconKey="launch" className="size-4 text-white" />
                    </span>
                );
            case LOOKUP_STATUS.FAILURE:
                return (
                    <span className="flex size-4 items-center justify-center text-green-500">
                        <Icon
                            iconKey="not-interested"
                            className="size-4 text-white"
                        />
                    </span>
                );
            case LOOKUP_STATUS.PENDING:
                return (
                    <div>
                        <Loading size={18} disableTimeout={true} />
                    </div>
                );
            default:
                return '';
        }
    };

    const renderLink = ({
        routeName,
        routeNumber,
        routeUrl,
        routeLink,
        type,
    }: OtherRoute) => (
        <CtiLinkIcon
            key={routeLink}
            routeName={routeName}
            routeNumber={routeNumber}
            routeUrl={routeUrl}
            routeLink={routeLink}
            type={type}
            getLinkOnClick={getLinkOnClick}
        />
    );

    const renderButton = ({ label, link, altLink }: Button) => (
        <CtiActionButton
            key={`button-${link}-${link}`}
            label={getTextLabel(label)}
            handleClick={getLinkOnClick(link, null, altLink)}
        />
    );

    const getBody = () => {
        switch (lookupStatus) {
            case LOOKUP_STATUS.SUCCESS: {
                const hasOtherRoutes =
                    !!otherRoutes && otherRoutes.length !== 0;
                const hasContactRouteButtons =
                    !!contactRouteButtons && contactRouteButtons.length !== 0;
                const renderBody =
                    hasOtherRoutes ||
                    hasContactRouteButtons ||
                    contactsMatchingNumber.size > 1;
                if (!renderBody) return null;
                return (
                    <div
                        className="mx-3 mt-0 mb-3"
                        data-test-id="cti-widget-lookup-success-container"
                    >
                        {(hasOtherRoutes ||
                            contactsMatchingNumber.size > 1) && (
                            <div
                                className="ms-2 mt-0"
                                data-test-id="cti-widget-outher-routes-container"
                            >
                                {contactsMatchingNumber.size > 1 && (
                                    <IntegrationContactSelector
                                        selectedCtiContact={selectedCtiContact}
                                        contactsMatchingNumber={
                                            contactsMatchingNumber
                                        }
                                    />
                                )}
                                {hasOtherRoutes &&
                                    limitOtherRoutes(otherRoutes).map(
                                        renderLink,
                                    )}
                            </div>
                        )}
                        {hasContactRouteButtons && (
                            <div
                                className="mt-2 flex flex-wrap justify-end gap-3 capitalize"
                                data-test-id="cti-widget-buttons-container"
                            >
                                {contactRouteButtons.map(renderButton)}
                            </div>
                        )}
                    </div>
                );
            }
            case LOOKUP_STATUS.FAILURE: {
                if (!defaultButtons || defaultButtons.length === 0) return null;
                return (
                    <div
                        className="mx-3 mt-0 mb-3"
                        data-test-id="cti-widget-lookup-fail-container"
                    >
                        <div className="mt-2 flex flex-row flex-wrap justify-end gap-3 capitalize">
                            {defaultButtons.map(renderButton)}
                        </div>
                    </div>
                );
            }
            case LOOKUP_STATUS.PENDING:
            default:
                return null;
        }
    };

    const getHeader = () => (
        <a
            className="focus-ring-inset flex items-center justify-between px-3 py-2 no-underline"
            href={contactRouteUrl}
            data-test-id="cti-widget-header"
            onClick={getLinkOnClick(contactRoute, contactRouteUrl)}
        >
            <div
                className="flex items-center gap-2"
                data-test-id="cti-widget-header-description"
            >
                {getHostIcon()}
                <div className="text-xs font-bold text-white">
                    {hostDescription}&nbsp;
                </div>
            </div>
            <div
                className="flex items-center gap-2 overflow-hidden"
                data-test-id="cti-widget-header-status"
            >
                <div className="overflow-hidden text-xs font-light text-ellipsis whitespace-nowrap text-gray-400">
                    {getStatusText()}
                </div>
                <div className="ps-2">{getStatusIcon()}</div>
            </div>
        </a>
    );

    return (
        <div
            className="flex flex-col overflow-hidden bg-gray-800 text-xs text-ellipsis whitespace-nowrap text-white"
            data-test-id="cti-widget-container"
        >
            {getHeader()}
            {getBody()}
        </div>
    );
};

export default React.memo(CtiIntegrationWidget);
